import { Box, HStack, IconButton, Input, Span } from '@chakra-ui/react';
import { Check, Copy, EditPencil } from 'iconoir-react';
import { type ComponentProps, forwardRef, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ClipboardIndicator, ClipboardRoot, SafeClipboardTrigger } from '@/components/ui/clipboard';

type RailHoverIconButtonProps = ComponentProps<typeof IconButton>;

/**
 * Ghost icon button that stays invisible until the row is hovered/focused — the
 * shared hover-reveal affordance behind both the copy and edit triggers.
 * forwardRef so the shared safe clipboard button can attach its ref and handler.
 */
const RailHoverIconButton = forwardRef<HTMLButtonElement, RailHoverIconButtonProps>(
  (props, ref) => (
    <IconButton
      ref={ref}
      variant="ghost"
      size="2xs"
      color="primary.400"
      opacity="0"
      transition="opacity 0.12s"
      _groupHover={{ opacity: 1 }}
      _focusVisible={{ opacity: 1 }}
      _hover={{ bg: 'whiteSmoke', color: 'dark' }}
      {...props}
    />
  )
);
RailHoverIconButton.displayName = 'RailHoverIconButton';

interface EditableContactFieldProps {
  /** Translated field label (e.g. Email, Téléphone). */
  label: string;
  /** Fixed label-column width so rows align with the read-only rows. */
  labelWidth: string;
  /** Current value (may be empty). */
  value: string;
  /** Placeholder shown when the value is empty. */
  placeholder?: string;
  /**
   * Optional client-side validator: returns a translated error message for an
   * invalid draft, or `null` when it is valid. An empty draft is treated as
   * valid (clearing the field). Commit is blocked while the draft is invalid.
   */
  validate?: (value: string) => string | null;
  /**
   * Called with the edited text when the field commits (blur or Enter) and the
   * draft actually changed. Optional: when omitted the field is presentational
   * (edits are kept locally but never lifted).
   */
  onCommit?: (value: string) => void;
  /**
   * Locks the field to its read view: no pencil, no click-to-edit. Used when no
   * creator identity is resolved yet, so an edit could not persist — better to
   * show the value plainly than to offer an edit that silently discards.
   */
  readOnly?: boolean;
}

/**
 * Contact field that reads like plain text, revealing an edit pencil on the right
 * on hover/focus. Clicking the pencil (or the text) swaps to an outline Input
 * (house rule: inputs are always `variant="outline"`). The input holds a local
 * draft seeded from `value`; blur or Enter commits it via `onCommit` (only when
 * it changed and it passes `validate`), Escape reverts. An invalid draft shows an
 * inline error and blocks the commit.
 */
const EditableContactField: React.FC<EditableContactFieldProps> = ({
  label,
  labelWidth,
  value,
  placeholder,
  validate,
  onCommit,
  readOnly = false,
}) => {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editing) inputRef.current?.focus();
  }, [editing]);

  const startEditing = () => {
    if (readOnly) return;
    setDraft(value);
    setError(null);
    setEditing(true);
  };

  const handleChange = (next: string) => {
    setDraft(next);
    // Clear a shown error as soon as the draft becomes valid again.
    if (error) setError(validate ? validate(next) : null);
  };

  const commit = () => {
    const validationError = validate ? validate(draft) : null;
    if (validationError) {
      setError(validationError);
      return; // stay in edit mode; do not commit invalid data
    }
    setError(null);
    setEditing(false);
    if (draft !== value) onCommit?.(draft);
  };

  const cancel = () => {
    setDraft(value);
    setError(null);
    setEditing(false);
  };

  return (
    <HStack
      gap="2.5"
      minH="38px"
      px="8px"
      rounded="7px"
      align={error ? 'start' : 'center'}
      role="group"
      transition="background 0.12s"
      _hover={readOnly ? undefined : { bg: 'babyPowder' }}
    >
      <Span
        flex="none"
        width={labelWidth}
        mt={error ? '1.5' : undefined}
        fontFamily="body"
        fontSize="xs"
        color="primary.400"
        whiteSpace="nowrap"
      >
        {label}
      </Span>

      {editing ? (
        <Box flex="1" minW="0">
          {/* Invisible, edit-in-place input: no box/border, matches the read text
              exactly, so clicking to edit never shifts the row height. */}
          <Input
            ref={inputRef}
            unstyled
            width="full"
            minW="0"
            h="auto"
            px="0"
            py="0"
            border="none"
            outline="none"
            appearance="none"
            bg="transparent"
            fontFamily="body"
            fontSize="13px"
            lineHeight="1.5"
            color="primary.900"
            value={draft}
            onChange={(e) => handleChange(e.target.value)}
            placeholder={placeholder}
            aria-label={label}
            aria-invalid={error ? true : undefined}
            _placeholder={{ color: 'primary.400' }}
            _focusVisible={{ outline: 'none', boxShadow: 'none' }}
            onBlur={commit}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                commit();
              } else if (e.key === 'Escape') {
                cancel();
              }
            }}
          />
          {error && (
            <Span display="block" mt="0.5" fontFamily="body" fontSize="11px" color="red.500">
              {error}
            </Span>
          )}
        </Box>
      ) : (
        <HStack flex="1" minW="0" gap="1.5" justify="space-between">
          <Span
            flex="1"
            minW="0"
            fontFamily="body"
            fontSize="13px"
            lineHeight="1.5"
            color={value ? 'primary.900' : 'primary.400'}
            overflow="hidden"
            textOverflow="ellipsis"
            whiteSpace="nowrap"
            cursor={readOnly ? 'default' : 'text'}
            onClick={readOnly ? undefined : startEditing}
          >
            {value || placeholder}
          </Span>
          <HStack gap="0.5" flex="none" align="center">
            {value && (
              <ClipboardRoot value={value}>
                <SafeClipboardTrigger>
                  <RailHoverIconButton aria-label={t('contactDrawer.rail.contact.copy')}>
                    <ClipboardIndicator copied={<Check width={13} height={13} />}>
                      <Copy width={13} height={13} />
                    </ClipboardIndicator>
                  </RailHoverIconButton>
                </SafeClipboardTrigger>
              </ClipboardRoot>
            )}
            {!readOnly && (
              <RailHoverIconButton
                aria-label={t('contactDrawer.rail.contact.edit', { field: label })}
                onClick={startEditing}
              >
                <EditPencil width={14} height={14} />
              </RailHoverIconButton>
            )}
          </HStack>
        </HStack>
      )}
    </HStack>
  );
};

export default EditableContactField;
